import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  ExternalLink,
  Package,
  AlertCircle,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { detectCarrier, CARRIERS } from "@/lib/carrierDetect";

const Track = () => {
  const [params, setParams] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [result, setResult] = useState(null);

  useEffect(() => {
    const q = params.get("id");
    if (q) {
      setId(q);
      setResult(detectCarrier(q));
    }
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    if (!id.trim()) return;
    setParams({ id: id.trim() });
    setResult(detectCarrier(id.trim()));
  };

  return (
    <div className="pt-16 md:pt-24">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-zam-orange)] font-semibold">
          Track your shipment
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-bold text-[var(--tt-navy)] mt-4 leading-[0.98]">
          Where&apos;s my package?
        </h1>
        <p className="mt-5 text-lg text-[var(--tt-ink-soft)]">
          Enter your tracking number below. We&apos;ll detect the carrier and take you to their live tracking.
        </p>

        <form
          onSubmit={submit}
          className="mt-8 flex items-center gap-2 rounded-2xl border border-[var(--tt-line)] bg-white pl-4 pr-2 py-2 focus-within:border-[var(--tt-green)] transition"
        >
          <Search className="h-5 w-5 text-[var(--tt-muted)]" />
          <input
            data-testid="track-input"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="e.g. 1Z999AA10123456784 or 9400111899223197428496"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[var(--tt-muted)]"
            autoFocus
          />
          <button
            data-testid="track-submit"
            type="submit"
            className="btn-primary !py-2.5 !px-5 text-sm"
          >
            Track <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        {result && result.matches.length > 0 && (
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--tt-muted)]">
              <Sparkles className="h-3.5 w-3.5 text-[var(--tt-zam-orange)]" />
              We found {result.matches.length}{" "}
              {result.matches.length === 1 ? "match" : "possible matches"}
            </div>

            {result.matches.map((c) => (
              <a
                key={c.key}
                href={c.url(result.number)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`carrier-${c.key}`}
                className="card block p-6 group hover:!border-[var(--tt-green)]"
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div
                      className="h-14 w-14 rounded-xl grid place-items-center font-display font-bold text-xs"
                      style={{
                        background: c.color,
                        color: c.dark,
                      }}
                    >
                      {c.logo}
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--tt-navy)] text-lg">
                        {c.name}
                      </div>
                      <div className="font-mono text-xs text-[var(--tt-muted)] mt-1 break-all">
                        {result.number}
                      </div>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tt-navy)] group-hover:bg-[var(--tt-green)] text-white px-4 py-2.5 text-sm font-semibold transition">
                    View live tracking <ExternalLink className="h-3.5 w-3.5" />
                  </span>
                </div>
              </a>
            ))}

            <div className="rounded-xl border border-[var(--tt-line)] bg-[var(--tt-bg-soft)] p-4 text-xs text-[var(--tt-muted)] flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-[var(--tt-zam-orange)]" />
              <span>
                Not the right carrier? Try our{" "}
                <a
                  href={result.fallbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--tt-navy)] font-semibold hover:text-[var(--tt-zam-orange)] transition underline underline-offset-2"
                >
                  multi-carrier tracker
                </a>{" "}
                — searches 1,000+ carriers worldwide.
              </span>
            </div>
          </div>
        )}

        {result && result.matches.length === 0 && (
          <div className="mt-10 card p-8 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[var(--tt-zam-orange-soft)] grid place-items-center">
              <AlertCircle className="h-7 w-7 text-[var(--tt-zam-orange)]" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[var(--tt-navy)] mt-5">
              We couldn&apos;t auto-detect the carrier.
            </h2>
            <p className="mt-2 text-[var(--tt-ink-soft)] max-w-md mx-auto">
              No worries — our multi-carrier tracker searches over 1,000
              carriers worldwide. It&apos;ll find your shipment.
            </p>
            <a
              href={result.fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-6 inline-flex"
            >
              Search all carriers <ExternalLink className="h-4 w-4" />
            </a>
            <div className="mt-6 text-xs text-[var(--tt-muted)]">
              Or try{" "}
              <a
                href={`https://www.17track.net/en/track#nums=${encodeURIComponent(result.number)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--tt-navy)] font-semibold hover:text-[var(--tt-zam-orange)] transition underline underline-offset-2"
              >
                17TRACK
              </a>{" "}
              as an alternative.
            </div>
          </div>
        )}

        {!result && (
          <div className="mt-10 card p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--tt-muted)] mb-4">
              <Package className="h-3.5 w-3.5" />
              Supported carriers
            </div>
            <div className="flex flex-wrap gap-2">
              {CARRIERS.map((c) => (
                <span
                  key={c.key}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tt-bg-soft)] border border-[var(--tt-line)] px-3 py-1.5 text-xs font-medium text-[var(--tt-ink-soft)]"
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: c.color }}
                  />
                  {c.name}
                </span>
              ))}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tt-navy)] text-white px-3 py-1.5 text-xs font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-zam-orange)]" />
                +1,000 more via multi-carrier lookup
              </span>
            </div>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-[var(--tt-muted)]">
          Powered by carrier auto-detection · No account or API key required
        </p>
      </div>
    </div>
  );
};

export default Track;
